import { useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Scales, ArrowRight, CheckCircle, WarningCircle, CurrencyDollar,
  TrendUp, TrendDown, ArrowsLeftRight, Lightning, ChartLineUp,
  WarningDiamond, Bank, Percent, Handshake, CreditCard, Buildings,
  House, ShieldCheck, Vault, PiggyBank, Scroll, Briefcase
} from "@phosphor-icons/react";

/* ============================================================
   LEVERAGE TAB
   ============================================================ */
function LeverageTab() {
  return (
    <div className="p-5 max-w-[1400px] space-y-6" data-testid="leverage-tab">
      {/* Hero */}
      <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-sm bg-orange-900/20 flex items-center justify-center">
            <Scales size={20} weight="bold" className="text-orange-400" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-zinc-50">What is Leverage?</h2>
            <p className="text-xs text-zinc-400">Using borrowed capital to amplify potential returns — and potential losses</p>
          </div>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">
          Leverage is the use of borrowed money (debt) to amplify the results of an investment or trade. Instead of using only your own money, you borrow additional capital so that every dollar of your own money controls more assets. If your trade is right, you earn outsized returns. If your trade is wrong, your losses are equally amplified — and you still owe what you borrowed.
        </p>
      </div>

      {/* Simple explanation */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm overflow-hidden">
        <div className="p-5 border-b border-zinc-800/50">
          <h3 className="text-sm font-heading font-bold text-zinc-100 flex items-center gap-2">
            <Lightning size={14} weight="bold" className="text-orange-400" /> How Leverage Works — Plain English
          </h3>
        </div>
        <div className="p-5 space-y-3">
          {[
            "You have $1,000 of your own money. You want to buy an asset that costs $10,000.",
            "You borrow $9,000 from a broker, bank, or lending platform. Now you control $10,000 worth of assets with only $1,000 of your own capital. That's 10x leverage.",
            "If the asset goes up 10% ($10,000 becomes $11,000), you sell and repay the $9,000 loan. You keep $2,000 — that's a 100% return on your $1,000, instead of a 10% return without leverage.",
            "If the asset drops 10% ($10,000 becomes $9,000), you still owe $9,000. Your entire $1,000 is wiped out — a 100% loss. With higher leverage or bigger drops, you can owe more than you started with.",
            "Leverage doesn't change the direction of a trade. It magnifies the outcome in both directions — wins become bigger wins, losses become bigger losses.",
          ].map((step, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-orange-900/30 text-orange-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
              <p className="text-xs text-zinc-300 leading-relaxed">{step}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Types of leverage */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-5">
        <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-4 font-medium">Common Types of Leverage</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-zinc-950/50 border border-zinc-800/40 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Bank size={14} weight="bold" className="text-blue-400" />
              <span className="text-xs font-medium text-zinc-200">Margin Trading</span>
            </div>
            <p className="text-lg font-data font-bold text-blue-400 mb-1">2x – 125x</p>
            <p className="text-[10px] text-zinc-500 leading-relaxed">You deposit collateral (margin) and the exchange lends you the rest. Common in crypto (Binance, Bybit) and stocks. Liquidation occurs if losses approach your collateral.</p>
          </div>
          <div className="bg-zinc-950/50 border border-zinc-800/40 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <ChartLineUp size={14} weight="bold" className="text-emerald-400" />
              <span className="text-xs font-medium text-zinc-200">Real Estate</span>
            </div>
            <p className="text-lg font-data font-bold text-emerald-400 mb-1">4x – 20x</p>
            <p className="text-[10px] text-zinc-500 leading-relaxed">A mortgage is leverage. Put 5-20% down, the bank loans 80-95%. A $50K down payment controls a $250K property — that's 5x leverage. If the home value rises 10%, your equity doubles.</p>
          </div>
          <div className="bg-zinc-950/50 border border-zinc-800/40 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <CurrencyDollar size={14} weight="bold" className="text-amber-400" />
              <span className="text-xs font-medium text-zinc-200">Options Contracts</span>
            </div>
            <p className="text-lg font-data font-bold text-amber-400 mb-1">10x – 100x+</p>
            <p className="text-[10px] text-zinc-500 leading-relaxed">Options let you control 100 shares for a fraction of the price. A $500 call option can control $15,000 worth of stock. If the stock moves 5%, your option might move 50-100%.</p>
          </div>
        </div>
      </div>

      {/* Financial Examples */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm overflow-hidden">
        <div className="p-5 border-b border-zinc-800/50">
          <h3 className="text-sm font-heading font-bold text-zinc-100 flex items-center gap-2">
            <CurrencyDollar size={14} weight="bold" className="text-emerald-400" /> Financial Examples of Leverage
          </h3>
        </div>
        <div className="p-5 space-y-5">
          {/* Example 1 */}
          <div className="bg-zinc-950/40 border border-zinc-800/40 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Badge className="text-[8px] px-1.5 py-0.5 border-0 bg-blue-900/40 text-blue-400 h-4">CRYPTO</Badge>
              <span className="text-xs font-medium text-zinc-200">Bitcoin 10x Long on Binance</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <div className="flex justify-between text-[10px]">
                  <span className="text-zinc-500">Your Capital</span>
                  <span className="text-zinc-300">$1,000</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-zinc-500">Leverage</span>
                  <span className="text-orange-400 font-medium">10x</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-zinc-500">Position Size</span>
                  <span className="text-zinc-300">$10,000</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-zinc-500">Liquidation Price</span>
                  <span className="text-red-400">~10% drop from entry</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-[10px]">
                  <TrendUp size={10} weight="bold" className="text-emerald-400" />
                  <span className="text-zinc-500">BTC +5%</span>
                  <span className="text-emerald-400 font-medium ml-auto">You profit $500 (+50% on $1K)</span>
                </div>
                <div className="flex items-center gap-2 text-[10px]">
                  <TrendUp size={10} weight="bold" className="text-emerald-400" />
                  <span className="text-zinc-500">BTC +10%</span>
                  <span className="text-emerald-400 font-medium ml-auto">You profit $1,000 (+100% on $1K)</span>
                </div>
                <div className="flex items-center gap-2 text-[10px]">
                  <TrendDown size={10} weight="bold" className="text-red-400" />
                  <span className="text-zinc-500">BTC -5%</span>
                  <span className="text-red-400 font-medium ml-auto">You lose $500 (-50% on $1K)</span>
                </div>
                <div className="flex items-center gap-2 text-[10px]">
                  <TrendDown size={10} weight="bold" className="text-red-400" />
                  <span className="text-zinc-500">BTC -10%</span>
                  <span className="text-red-400 font-medium ml-auto">LIQUIDATED — $1,000 gone</span>
                </div>
              </div>
            </div>
          </div>

          {/* Example 2 */}
          <div className="bg-zinc-950/40 border border-zinc-800/40 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Badge className="text-[8px] px-1.5 py-0.5 border-0 bg-emerald-900/40 text-emerald-400 h-4">REAL ESTATE</Badge>
              <span className="text-xs font-medium text-zinc-200">Rental Property with Mortgage</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <div className="flex justify-between text-[10px]">
                  <span className="text-zinc-500">Property Value</span>
                  <span className="text-zinc-300">$300,000</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-zinc-500">Your Down Payment</span>
                  <span className="text-zinc-300">$60,000 (20%)</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-zinc-500">Mortgage (Borrowed)</span>
                  <span className="text-orange-400 font-medium">$240,000 (5x leverage)</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-[10px]">
                  <TrendUp size={10} weight="bold" className="text-emerald-400" />
                  <span className="text-zinc-500">Home +10% ($330K)</span>
                  <span className="text-emerald-400 font-medium ml-auto">Equity: $90K (+50% on $60K)</span>
                </div>
                <div className="flex items-center gap-2 text-[10px]">
                  <TrendUp size={10} weight="bold" className="text-emerald-400" />
                  <span className="text-zinc-500">+ Rental income $1,800/mo</span>
                  <span className="text-emerald-400 font-medium ml-auto">Cash flow on top of appreciation</span>
                </div>
                <div className="flex items-center gap-2 text-[10px]">
                  <TrendDown size={10} weight="bold" className="text-red-400" />
                  <span className="text-zinc-500">Home -20% ($240K)</span>
                  <span className="text-red-400 font-medium ml-auto">Equity: $0 — underwater</span>
                </div>
              </div>
            </div>
          </div>

          {/* Example 3 */}
          <div className="bg-zinc-950/40 border border-zinc-800/40 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Badge className="text-[8px] px-1.5 py-0.5 border-0 bg-violet-900/40 text-violet-400 h-4">OPTIONS</Badge>
              <span className="text-xs font-medium text-zinc-200">NVDA Call Option</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <div className="flex justify-between text-[10px]">
                  <span className="text-zinc-500">NVDA Stock Price</span>
                  <span className="text-zinc-300">$800</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-zinc-500">Call Option Cost (premium)</span>
                  <span className="text-zinc-300">$2,000 (controls 100 shares = $80,000)</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-zinc-500">Effective Leverage</span>
                  <span className="text-orange-400 font-medium">~40x</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-[10px]">
                  <TrendUp size={10} weight="bold" className="text-emerald-400" />
                  <span className="text-zinc-500">NVDA +10% ($880)</span>
                  <span className="text-emerald-400 font-medium ml-auto">Option worth ~$10K (+400%)</span>
                </div>
                <div className="flex items-center gap-2 text-[10px]">
                  <TrendDown size={10} weight="bold" className="text-red-400" />
                  <span className="text-zinc-500">NVDA flat or down at expiry</span>
                  <span className="text-red-400 font-medium ml-auto">Option expires worthless — $2K gone</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pros and Cons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-zinc-900/40 border border-emerald-900/30 rounded-sm p-5">
          <h3 className="text-[10px] uppercase tracking-wider text-emerald-400 mb-3 font-medium flex items-center gap-1.5">
            <CheckCircle size={12} weight="bold" /> When Leverage Works
          </h3>
          <ul className="space-y-2.5">
            {[
              "Amplifies gains — Turn a 5% move into a 50% return with 10x leverage.",
              "Capital efficiency — Control large positions with small capital, freeing up cash for diversification.",
              "Real estate wealth building — Most millionaires built wealth through leveraged property, not by paying cash.",
              "Business growth — Companies use debt (leverage) to expand faster than retained earnings alone would allow.",
              "Hedging — Use leveraged derivatives to protect existing positions cheaply.",
            ].map((pro, i) => (
              <li key={i} className="flex items-start gap-2">
                <ArrowRight size={10} weight="bold" className="text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-zinc-300 leading-relaxed">{pro}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-zinc-900/40 border border-red-900/30 rounded-sm p-5">
          <h3 className="text-[10px] uppercase tracking-wider text-red-400 mb-3 font-medium flex items-center gap-1.5">
            <WarningCircle size={12} weight="bold" /> When Leverage Kills
          </h3>
          <ul className="space-y-2.5">
            {[
              "Amplifies losses equally — A 10% drop with 10x leverage = 100% loss (liquidation).",
              "Liquidation cascades — In crypto, mass liquidations cause price crashes that liquidate even more traders.",
              "Interest and fees — Borrowed money isn't free. Funding rates on crypto perpetuals can drain your position over time.",
              "Margin calls — In stocks and real estate, you may be forced to add capital or sell at the worst possible time.",
              "Emotional pressure — Watching leveraged positions swing 50%+ intraday leads to panic decisions and poor risk management.",
              "Blows up accounts — The #1 reason new traders go to zero is overleveraging, not bad trade direction.",
            ].map((con, i) => (
              <li key={i} className="flex items-start gap-2">
                <ArrowRight size={10} weight="bold" className="text-red-400 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-zinc-300 leading-relaxed">{con}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Risk table */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-5">
        <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 font-medium">Leverage vs. Liquidation Distance</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-[10px]" data-testid="leverage-liquidation-table">
            <thead>
              <tr className="border-b border-zinc-800/60">
                <th className="text-left px-3 py-2 text-zinc-500 font-medium">Leverage</th>
                <th className="text-center px-3 py-2 text-zinc-500 font-medium">Position Size ($1K capital)</th>
                <th className="text-center px-3 py-2 text-zinc-500 font-medium">Price Drop to Liquidation</th>
                <th className="text-center px-3 py-2 text-zinc-500 font-medium">Profit if +10% Move</th>
                <th className="text-center px-3 py-2 text-zinc-500 font-medium">Risk Level</th>
              </tr>
            </thead>
            <tbody>
              {[
                { lev: "2x", pos: "$2,000", liq: "~50%", profit: "+$200 (20%)", risk: "Low", color: "text-emerald-400" },
                { lev: "5x", pos: "$5,000", liq: "~20%", profit: "+$500 (50%)", risk: "Medium", color: "text-amber-400" },
                { lev: "10x", pos: "$10,000", liq: "~10%", profit: "+$1,000 (100%)", risk: "High", color: "text-orange-400" },
                { lev: "25x", pos: "$25,000", liq: "~4%", profit: "+$2,500 (250%)", risk: "Very High", color: "text-red-400" },
                { lev: "50x", pos: "$50,000", liq: "~2%", profit: "+$5,000 (500%)", risk: "Extreme", color: "text-red-500" },
                { lev: "100x", pos: "$100,000", liq: "~1%", profit: "+$10,000 (1000%)", risk: "Degen", color: "text-red-600" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-zinc-800/30 hover:bg-zinc-900/30">
                  <td className="px-3 py-2 text-zinc-200 font-medium">{row.lev}</td>
                  <td className="px-3 py-2 text-zinc-300 text-center">{row.pos}</td>
                  <td className="px-3 py-2 text-zinc-300 text-center">{row.liq}</td>
                  <td className="px-3 py-2 text-emerald-400 text-center">{row.profit}</td>
                  <td className={`px-3 py-2 text-center font-medium ${row.color}`}>{row.risk}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   HOW TO LEVERAGE TAB
   ============================================================ */
function HowToLeverageTab() {
  const sources = [
    {
      icon: Bank, color: "text-blue-400", bg: "bg-blue-900/20",
      title: "Bank Business Loans",
      subtitle: "SBA loans, term loans, lines of credit",
      how: "Banks lend money to businesses and individuals based on creditworthiness, collateral, and income. An SBA loan might let you borrow $500K at 6-8% interest to buy a business that generates $150K/year in profit. Your down payment might only be $50K — that's 10x leverage on your capital.",
      example: "You put $50K down on a laundromat business valued at $500K using an SBA loan. The business nets $120K/year after loan payments. That's a 240% annual return on your $50K investment.",
      tip: "Build business credit separately from personal credit. Start with a business checking account, get a business credit card, and establish trade lines with vendors who report to Dun & Bradstreet.",
      rates: "5% – 10% APR",
      access: "Moderate — requires business plan, credit score 680+, and often collateral"
    },
    {
      icon: CreditCard, color: "text-violet-400", bg: "bg-violet-900/20",
      title: "Credit Cards (0% APR)",
      subtitle: "Balance transfer offers, business credit cards",
      how: "Many credit cards offer 0% APR for 12-21 months on purchases or balance transfers. This is essentially an interest-free loan. You can use this capital to invest, fund a business, or cover expenses while your own money works elsewhere. The key is paying it off before the promotional period ends.",
      example: "You get 3 business credit cards with $15K limits each = $45K at 0% APR for 18 months. You use $45K to buy inventory for a reselling business. Over 18 months you turn that into $90K in revenue and pay off the cards. Your cost of capital was $0.",
      tip: "Never carry a balance past the 0% window — standard APR jumps to 20-29%. Use balance transfer cards strategically, not as a lifestyle crutch. Set calendar reminders 2 months before the promo expires.",
      rates: "0% for 12–21 months, then 20–29% APR",
      access: "Easy — requires good personal credit (720+) for the best offers"
    },
    {
      icon: Scroll, color: "text-amber-400", bg: "bg-amber-900/20",
      title: "Signature Loans (Personal Loans)",
      subtitle: "Unsecured loans based on your signature and credit",
      how: "A signature loan is an unsecured personal loan — no collateral required. The bank lends you money purely based on your credit score, income, and repayment history. You sign a promissory note (hence 'signature loan') and receive a lump sum. These can be used for anything: investing, business, debt consolidation.",
      example: "You take a $30K signature loan at 7% APR and use it as a down payment on a $150K rental property. The rental income covers the loan payment plus the mortgage, and you've acquired a $150K asset with zero of your own money — infinite leverage.",
      tip: "Credit unions often offer better rates than big banks for signature loans. Build relationships with local credit unions — they're more flexible with lending criteria and may approve higher amounts.",
      rates: "6% – 15% APR depending on credit",
      access: "Moderate — requires strong credit (700+) and verifiable income"
    },
    {
      icon: Buildings, color: "text-emerald-400", bg: "bg-emerald-900/20",
      title: "LLC Business Credit",
      subtitle: "Separate legal entity with its own credit profile",
      how: "An LLC (Limited Liability Company) can build its own credit profile separate from your personal credit. Over time, your LLC can qualify for business credit cards, lines of credit, and loans based on the business's revenue and credit history — not yours. This lets you access capital without putting your personal credit at risk.",
      example: "Your LLC builds business credit over 2 years. It qualifies for a $100K business line of credit at 8% APR. You use $80K to fund a real estate flip, sell the property for a $40K profit, repay the line, and repeat. Your personal credit was never touched.",
      tip: "Step 1: Form LLC. Step 2: Get EIN from IRS. Step 3: Open business bank account. Step 4: Get a DUNS number. Step 5: Start with net-30 vendor accounts (Uline, Grainger, Quill) that report to business credit bureaus. Step 6: After 6 months, apply for business credit cards.",
      rates: "7% – 15% APR on lines of credit",
      access: "Takes 6-24 months to build — requires patience and consistent revenue"
    },
    {
      icon: House, color: "text-teal-400", bg: "bg-teal-900/20",
      title: "Home Equity (HELOC / Cash-Out Refi)",
      subtitle: "Borrow against the equity in your real estate",
      how: "If your home is worth more than you owe, the difference is equity. A Home Equity Line of Credit (HELOC) or cash-out refinance lets you borrow against that equity at low interest rates (since your home is the collateral). This is one of the cheapest forms of leverage available to regular people.",
      example: "Your home is worth $400K and you owe $250K. You have $150K in equity. You take a HELOC for $100K at 7% APR and use it as down payments on two rental properties worth $250K each. Now you control $500K in real estate funded by your home equity. The rentals pay for themselves plus the HELOC payment.",
      tip: "HELOCs have variable rates — consider locking a portion to a fixed rate if available. Never borrow your full equity; keep a 20% buffer. Use home equity for income-producing assets (rentals, businesses), not depreciating assets (cars, vacations).",
      rates: "6% – 9% APR (variable)",
      access: "Easy if you have equity — requires home appraisal, 620+ credit, and proof of income"
    },
    {
      icon: ShieldCheck, color: "text-blue-400", bg: "bg-blue-900/20",
      title: "Whole Life Insurance Policy Loans",
      subtitle: "Borrow against your policy's cash value",
      how: "Once your whole life insurance policy has built up cash value (usually after 5-10 years), you can borrow against it. The insurance company lends you money using your cash value as collateral. The loan doesn't appear on your credit report, requires no credit check, and your cash value continues to earn dividends even while the loan is outstanding.",
      example: "Your whole life policy has $80K in cash value. You borrow $60K at 5% from the insurance company. You use the $60K as a down payment on a $300K rental property. The rental income covers the property mortgage AND the policy loan repayment. Meanwhile, your full $80K cash value still earns its 2% dividend.",
      tip: "The 'Infinite Banking Concept' (IBC) is built entirely on this strategy — using whole life as your own personal bank. Borrow from your policy, invest, repay yourself with interest. The key: the cash value never stops compounding, even while you're borrowing against it.",
      rates: "4% – 8% APR (set by insurance company)",
      access: "Easy once cash value exists — no credit check, no approval process, no questions asked"
    },
    {
      icon: ChartLineUp, color: "text-violet-400", bg: "bg-violet-900/20",
      title: "IUL (Indexed Universal Life) Loans",
      subtitle: "Tax-free leverage from your IUL cash value",
      how: "Similar to whole life, IULs build cash value that you can borrow against. The advantage: IUL cash values can grow faster (5-8% linked to the S&P 500 vs. whole life's 1-3%). Tax-free policy loans from an IUL are the primary way high-net-worth individuals create tax-free retirement income. You borrow against your cash value, never repay it, and the death benefit covers the loan when you die.",
      example: "After 20 years of funding an IUL with $1,000/month, you have $400K in cash value. You start taking $30K/year in tax-free policy loans for retirement income. Over 30 years of retirement, you take out $900K tax-free. When you die, the $1M death benefit pays off any outstanding loans and the rest goes to your family.",
      tip: "IUL loans work best when the policy is well-funded (overfunded beyond the minimum premium). The arbitrage: you borrow at 4-6% while your cash value earns 5-8% linked to the index. The spread between what you earn and what you pay is your profit. Structure this with a knowledgeable insurance advisor.",
      rates: "4% – 6% APR (often wash loans at 0% net cost)",
      access: "Requires a well-funded IUL with substantial cash value — typically 7+ years of consistent funding"
    },
    {
      icon: Vault, color: "text-emerald-400", bg: "bg-emerald-900/20",
      title: "Self-Directed IRA / Solo 401(k)",
      subtitle: "Use retirement funds to invest in real estate, businesses, and alternative assets",
      how: "A Self-Directed IRA (SDIRA) or Solo 401(k) lets you invest your retirement money in assets beyond stocks and bonds — including real estate, private businesses, promissory notes, tax liens, and crypto. A Solo 401(k) can even take a loan: you borrow up to $50K or 50% of the balance from your own retirement account and use it for anything.",
      example: "You have $200K in a Self-Directed IRA. You use it to buy a rental property for $180K (the IRA owns the property). All rental income goes back into the IRA tax-free. After 10 years the property is worth $350K — all that growth is tax-free inside the IRA.",
      tip: "Solo 401(k) loan is the cheat code: borrow $50K from your own retirement at prime rate + 1%, use it to invest outside the account, and repay yourself with interest. You're paying interest to yourself. For SDIRA real estate, all expenses and income must flow through the IRA — you can't personally benefit from the property.",
      rates: "Prime + 1% for Solo 401(k) loans; no cost for SDIRA direct investments",
      access: "SDIRA: any custodian that offers self-directed accounts. Solo 401(k): must have self-employment income"
    },
    {
      icon: PiggyBank, color: "text-rose-400", bg: "bg-rose-900/20",
      title: "Margin Loans Against Brokerage Accounts",
      subtitle: "Borrow against your stock portfolio without selling",
      how: "If you have a brokerage account with stocks, bonds, or ETFs, you can borrow against it using a margin loan or Portfolio Line of Credit (PLOC). This lets you access cash without selling your investments (avoiding capital gains taxes). Interactive Brokers offers some of the lowest margin rates in the industry.",
      example: "You have $500K in an index fund portfolio. You take a $150K margin loan at 5.5% APR and use it as a down payment on rental properties. Your portfolio keeps growing (averaging 10%/year), the rentals cash flow, and you avoided selling $150K worth of stock and paying $30K+ in capital gains taxes.",
      tip: "Keep your loan-to-value ratio under 30% to avoid margin calls during market dips. A $500K portfolio can safely support a $150K loan. If the market drops 40%, your portfolio is $300K and your loan is still $150K — that's 50% LTV, getting risky but not yet a margin call.",
      rates: "4% – 8% APR depending on broker and loan size",
      access: "Easy — any margin-enabled brokerage account with sufficient equity"
    },
    {
      icon: Briefcase, color: "text-sky-400", bg: "bg-sky-900/20",
      title: "Hard Money & Private Lending",
      subtitle: "Asset-based loans from private lenders — speed over credit score",
      how: "Hard money lenders are private individuals or companies that lend based primarily on the value of the asset (usually real estate), not your credit score. Loans are short-term (6-24 months), higher interest (8-15%), but close in days instead of months. Real estate investors use hard money to buy, rehab, and flip properties quickly.",
      example: "You find a house worth $200K selling for $120K because it needs $40K in repairs. A hard money lender gives you $130K (covering purchase + some rehab) at 12% interest for 12 months. You rehab in 4 months, sell for $210K, repay the lender $140K (principal + interest), and keep $30K profit. Total time: 6 months. Your own money used: $30K.",
      tip: "Hard money is a tool, not a long-term strategy. Use it to acquire and stabilize an asset, then refinance into a conventional loan at a lower rate (the BRRRR strategy: Buy, Rehab, Rent, Refinance, Repeat). Always have your exit strategy planned before taking hard money.",
      rates: "8% – 15% APR + 1-3 points origination",
      access: "Easy — primarily based on the deal/property, not your personal credit. Can close in 5-10 days."
    },
  ];

  return (
    <div className="p-5 max-w-[1400px] space-y-6" data-testid="how-to-leverage-tab">
      {/* Hero */}
      <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-sm bg-teal-900/20 flex items-center justify-center">
            <Handshake size={20} weight="bold" className="text-teal-400" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-zinc-50">How to Leverage — Sources of Capital</h2>
            <p className="text-xs text-zinc-400">Every vehicle available to borrow money and put it to work</p>
          </div>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">
          Wealthy people don't use their own money — they use other people's money (OPM). Banks, insurance companies, brokerages, and the government all offer ways to borrow capital at low rates and deploy it into assets that return more than the cost of borrowing. The spread between what you pay to borrow and what you earn on the investment is how wealth compounds. Below is every major source of leverage available to you.
        </p>
      </div>

      {/* The golden rule */}
      <div className="bg-amber-900/10 border border-amber-800/30 rounded-sm p-5">
        <h3 className="text-xs font-medium text-amber-300 mb-2 flex items-center gap-2">
          <CurrencyDollar size={14} weight="bold" /> The Golden Rule of Leverage
        </h3>
        <p className="text-xs text-zinc-300 leading-relaxed">
          <strong className="text-zinc-100">Only borrow money when the return on the investment exceeds the cost of borrowing.</strong> If you borrow at 7% APR, you need to earn more than 7% on whatever you put that money into. If a rental property returns 12% annually and your mortgage is 7%, the 5% spread is your profit — multiplied across the entire borrowed amount. This is how leverage builds wealth.
        </p>
      </div>

      {/* All leverage sources */}
      <div className="space-y-4">
        {sources.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm overflow-hidden" data-testid={`leverage-source-${i}`}>
              <div className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-9 h-9 rounded-sm ${s.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={18} weight="bold" className={s.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-heading font-bold text-zinc-100">{s.title}</h3>
                    <p className="text-[10px] text-zinc-500">{s.subtitle}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[9px] uppercase tracking-wider text-zinc-600">Typical Rates</p>
                    <p className={`text-xs font-data font-bold ${s.color}`}>{s.rates}</p>
                  </div>
                </div>

                <p className="text-xs text-zinc-300 leading-relaxed mb-3">{s.how}</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="bg-zinc-950/50 border border-emerald-900/20 rounded-sm p-3">
                    <p className="text-[9px] uppercase tracking-wider text-emerald-500 mb-1 font-medium">Real Example</p>
                    <p className="text-[10px] text-zinc-400 leading-relaxed">{s.example}</p>
                  </div>
                  <div className="bg-zinc-950/50 border border-amber-900/20 rounded-sm p-3">
                    <p className="text-[9px] uppercase tracking-wider text-amber-500 mb-1 font-medium">Pro Tip</p>
                    <p className="text-[10px] text-zinc-400 leading-relaxed">{s.tip}</p>
                  </div>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <p className="text-[9px] uppercase tracking-wider text-zinc-600">Access Difficulty:</p>
                  <p className="text-[10px] text-zinc-400">{s.access}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Strategy stack */}
      <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm overflow-hidden" data-testid="leverage-strategy-stack">
        <div className="p-5 border-b border-zinc-800/50">
          <h3 className="text-sm font-heading font-bold text-zinc-100 flex items-center gap-2">
            <Lightning size={14} weight="bold" className="text-amber-400" /> The Leverage Stack — Combining Sources
          </h3>
          <p className="text-xs text-zinc-500 mt-1">Advanced wealth builders don't use one source — they stack multiple leverage sources together</p>
        </div>
        <div className="p-5 space-y-3">
          {[
            { step: "Step 1 — Build Foundation", desc: "Max out Roth IRA ($7K/yr). Build personal credit to 750+. Open LLC and start building business credit. Fund a whole life or IUL policy.", color: "text-blue-400" },
            { step: "Step 2 — Access Capital", desc: "Use business credit cards (0% APR) for short-term capital. Take a signature loan for a down payment. Borrow against whole life/IUL cash value. Use HELOC if you own a home.", color: "text-emerald-400" },
            { step: "Step 3 — Deploy Into Assets", desc: "Buy rental properties, fund a business, or invest in cash-flowing assets. Use hard money for fix-and-flips. Use margin loans to avoid selling stock portfolios.", color: "text-amber-400" },
            { step: "Step 4 — Refinance and Repeat", desc: "Once assets stabilize, refinance short-term debt into long-term low-rate loans. Pull equity from appreciated properties. Repay policy loans and borrow again. Rinse and repeat.", color: "text-violet-400" },
            { step: "Step 5 — Scale", desc: "Your LLC now has revenue and credit. Banks lend to the business on its own merit. Your personal assets are protected. You control millions in assets with a fraction of your own capital. This is how the wealthy operate.", color: "text-orange-400" },
          ].map((s, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className={`w-6 h-6 rounded-full bg-zinc-800 ${s.color} flex items-center justify-center text-[10px] font-bold flex-shrink-0`}>{i + 1}</span>
              <div>
                <p className={`text-xs font-medium ${s.color}`}>{s.step}</p>
                <p className="text-[10px] text-zinc-400 leading-relaxed mt-0.5">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom line */}
      <div className="bg-teal-900/10 border border-teal-800/30 rounded-sm p-5" data-testid="how-leverage-bottom-line">
        <h3 className="text-xs font-medium text-teal-300 mb-3 flex items-center gap-2">
          <WarningDiamond size={14} weight="bold" /> The Bottom Line
        </h3>
        <div className="space-y-3">
          <p className="text-xs text-zinc-300 leading-relaxed">
            <strong className="text-zinc-100">Leverage is a tool — it amplifies who you already are.</strong> If you're disciplined and educated, leverage accelerates wealth building. If you're reckless, it accelerates financial ruin. Every source listed above is legal, widely used by the wealthy, and available to anyone willing to learn and build their credit and financial profile.
          </p>
          <p className="text-xs text-zinc-300 leading-relaxed">
            <strong className="text-zinc-100">Start small, learn the mechanics, and scale.</strong> Don't try to use every leverage source at once. Master one (like real estate with a HELOC), prove the model works, then add another layer. The wealthiest people in the world didn't avoid debt — they understood it and used it strategically.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   ARBITRAGE TAB
   ============================================================ */
function ArbitrageTab() {
  return (
    <div className="p-5 max-w-[1400px] space-y-6" data-testid="arbitrage-tab">
      {/* Hero */}
      <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-sm bg-cyan-900/20 flex items-center justify-center">
            <ArrowsLeftRight size={20} weight="bold" className="text-cyan-400" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-zinc-50">What is Arbitrage?</h2>
            <p className="text-xs text-zinc-400">Profiting from price differences of the same asset across different markets — simultaneously</p>
          </div>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">
          Arbitrage is the practice of buying an asset on one market and simultaneously selling it on another market at a higher price, pocketing the difference as risk-free profit. The key word is "simultaneously" — true arbitrage has no directional risk because you're not betting the price will go up or down. You're exploiting an existing price discrepancy that the market hasn't corrected yet.
        </p>
      </div>

      {/* How it works */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm overflow-hidden">
        <div className="p-5 border-b border-zinc-800/50">
          <h3 className="text-sm font-heading font-bold text-zinc-100 flex items-center gap-2">
            <ArrowsLeftRight size={14} weight="bold" className="text-cyan-400" /> How Arbitrage Works
          </h3>
        </div>
        <div className="p-5 space-y-3">
          {[
            "You notice Bitcoin is trading at $83,000 on Coinbase and $83,400 on Kraken at the same time.",
            "You buy 1 BTC on Coinbase for $83,000 and simultaneously sell 1 BTC on Kraken for $83,400.",
            "You pocket the $400 difference minus any transaction fees and transfer costs.",
            "The trade is risk-free in theory because both legs happen at the same time — you're not exposed to price movement.",
            "In practice, speed is everything. These price gaps close in milliseconds. Professionals use bots and co-located servers to execute faster than humans can blink.",
            "Arbitrage is a fundamental market force — by exploiting price differences, arbitrageurs help keep prices consistent across markets. They're providing a service to the market."
          ].map((step, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-cyan-900/30 text-cyan-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
              <p className="text-xs text-zinc-300 leading-relaxed">{step}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Types of arbitrage */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-5">
        <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-4 font-medium">Types of Arbitrage</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-zinc-950/50 border border-zinc-800/40 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <ArrowsLeftRight size={14} weight="bold" className="text-cyan-400" />
              <span className="text-xs font-medium text-zinc-200">Spatial Arbitrage</span>
            </div>
            <p className="text-[10px] text-zinc-500 leading-relaxed mb-2">Same asset, different exchanges. Buy BTC on Exchange A, sell on Exchange B. The most straightforward form — exploits geographic or platform pricing differences.</p>
            <p className="text-[9px] text-cyan-400/80">Example: BTC $83,000 on Coinbase vs $83,350 on Binance = $350 profit per BTC</p>
          </div>
          <div className="bg-zinc-950/50 border border-zinc-800/40 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Percent size={14} weight="bold" className="text-violet-400" />
              <span className="text-xs font-medium text-zinc-200">Triangular Arbitrage</span>
            </div>
            <p className="text-[10px] text-zinc-500 leading-relaxed mb-2">Exploiting price discrepancies between three trading pairs on the same exchange. Trade USD to BTC, BTC to ETH, ETH to USD — and end up with more USD than you started.</p>
            <p className="text-[9px] text-violet-400/80">Example: $10,000 USD → BTC → ETH → $10,080 USD = $80 risk-free</p>
          </div>
          <div className="bg-zinc-950/50 border border-zinc-800/40 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <ChartLineUp size={14} weight="bold" className="text-emerald-400" />
              <span className="text-xs font-medium text-zinc-200">Statistical Arbitrage</span>
            </div>
            <p className="text-[10px] text-zinc-500 leading-relaxed mb-2">Using mathematical models to identify assets that historically move together. When they deviate, you short the expensive one and long the cheap one, expecting them to converge.</p>
            <p className="text-[9px] text-emerald-400/80">Example: BTC and ETH usually move in sync. When ETH lags behind a BTC rally, go long ETH / short BTC</p>
          </div>
          <div className="bg-zinc-950/50 border border-zinc-800/40 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Lightning size={14} weight="bold" className="text-amber-400" />
              <span className="text-xs font-medium text-zinc-200">DeFi / Flash Loan Arbitrage</span>
            </div>
            <p className="text-[10px] text-zinc-500 leading-relaxed mb-2">Borrow millions with zero collateral using flash loans (must repay in the same transaction). Buy cheap on DEX A, sell expensive on DEX B, repay loan, keep profit — all in one block.</p>
            <p className="text-[9px] text-amber-400/80">Example: Borrow $1M USDC → buy ETH on Uniswap → sell ETH on SushiSwap → repay + keep $2,000</p>
          </div>
        </div>
      </div>

      {/* Real world examples */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm overflow-hidden">
        <div className="p-5 border-b border-zinc-800/50">
          <h3 className="text-sm font-heading font-bold text-zinc-100 flex items-center gap-2">
            <CurrencyDollar size={14} weight="bold" className="text-emerald-400" /> Real-World Financial Examples
          </h3>
        </div>
        <div className="p-5 space-y-4">
          {[
            {
              title: "Kimchi Premium (Crypto)",
              badge: "CRYPTO", badgeColor: "bg-blue-900/40 text-blue-400",
              desc: "South Korean exchanges (Upbit, Bithumb) frequently price Bitcoin 3-8% higher than global exchanges due to capital controls and local demand. Traders with access to both markets buy BTC globally and sell in Korea. The challenge: South Korean banking regulations make it hard to move money in/out, which is why the premium persists."
            },
            {
              title: "Forex Carry Trade",
              badge: "FOREX", badgeColor: "bg-emerald-900/40 text-emerald-400",
              desc: "Borrow money in Japan (0.1% interest rate) and invest it in Australia (4.5% interest rate). You earn the 4.4% interest rate difference. This is a form of leveraged arbitrage on interest rates. Risk: if the Japanese Yen strengthens against the Australian Dollar, the currency loss can wipe out your interest gains."
            },
            {
              title: "CEX vs. DEX Arbitrage (Crypto)",
              badge: "DEFI", badgeColor: "bg-violet-900/40 text-violet-400",
              desc: "Token prices on decentralized exchanges (Uniswap, PulseX) often differ from centralized exchanges (Coinbase, Binance) because DEX prices are set by automated market makers (AMMs), not order books. Bots constantly exploit these differences — buy cheap on the DEX, sell expensive on the CEX, or vice versa."
            },
            {
              title: "Merger Arbitrage (Stocks)",
              badge: "STOCKS", badgeColor: "bg-amber-900/40 text-amber-400",
              desc: "When Company A announces it will buy Company B at $50/share, Company B's stock might trade at $48 (the $2 gap reflects risk the deal might fall through). Arbitrageurs buy at $48, and if the deal closes, they collect $50. Risk-adjusted return on dozens of deals adds up."
            },
            {
              title: "Stablecoin Depeg Arbitrage (Crypto)",
              badge: "DEFI", badgeColor: "bg-violet-900/40 text-violet-400",
              desc: "When a stablecoin like USDC temporarily trades at $0.98 instead of $1.00 due to market panic, arbitrageurs buy millions of USDC at $0.98 knowing they can redeem it for $1.00 from Circle (the issuer). That's a 2% risk-free return — on a million dollars, that's $20,000."
            },
          ].map((ex, i) => (
            <div key={i} className="bg-zinc-950/40 border border-zinc-800/40 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Badge className={`text-[8px] px-1.5 py-0.5 border-0 ${ex.badgeColor} h-4`}>{ex.badge}</Badge>
                <span className="text-xs font-medium text-zinc-200">{ex.title}</span>
              </div>
              <p className="text-[10px] text-zinc-400 leading-relaxed">{ex.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Pros and Cons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-zinc-900/40 border border-emerald-900/30 rounded-sm p-5">
          <h3 className="text-[10px] uppercase tracking-wider text-emerald-400 mb-3 font-medium flex items-center gap-1.5">
            <CheckCircle size={12} weight="bold" /> Advantages
          </h3>
          <ul className="space-y-2.5">
            {[
              "Theoretically risk-free — True arbitrage has no directional market risk since you buy and sell simultaneously.",
              "Consistent small gains — Arbitrage strategies aim for many small, reliable profits rather than big swings.",
              "Market efficiency — Arbitrageurs serve the market by keeping prices in sync across exchanges.",
              "Scalable with capital — The more money you deploy, the more you earn from the same spread.",
              "Works in any market condition — Arbitrage opportunities exist in bull, bear, and sideways markets.",
            ].map((pro, i) => (
              <li key={i} className="flex items-start gap-2">
                <ArrowRight size={10} weight="bold" className="text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-zinc-300 leading-relaxed">{pro}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-zinc-900/40 border border-red-900/30 rounded-sm p-5">
          <h3 className="text-[10px] uppercase tracking-wider text-red-400 mb-3 font-medium flex items-center gap-1.5">
            <WarningCircle size={12} weight="bold" /> Risks and Challenges
          </h3>
          <ul className="space-y-2.5">
            {[
              "Execution risk — If one leg of the trade fails (exchange lag, network congestion), you're left exposed to price movement.",
              "Fees eat profits — Trading fees, withdrawal fees, gas fees, and transfer times can erase small spreads.",
              "Speed competition — You're racing against institutional bots with co-located servers and sub-millisecond execution.",
              "Capital lockup — Funds can get stuck on an exchange during transfers, tying up capital and increasing risk.",
              "Smart contract risk (DeFi) — Flash loan arbitrage can fail due to bugs, and MEV bots can front-run your transactions.",
              "Regulatory risk — Cross-border arbitrage can trigger tax complications and violate capital movement rules.",
            ].map((con, i) => (
              <li key={i} className="flex items-start gap-2">
                <ArrowRight size={10} weight="bold" className="text-red-400 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-zinc-300 leading-relaxed">{con}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Bottom line */}
      <div className="bg-cyan-900/10 border border-cyan-800/30 rounded-sm p-5" data-testid="arbitrage-bottom-line">
        <h3 className="text-xs font-medium text-cyan-300 mb-3 flex items-center gap-2">
          <WarningDiamond size={14} weight="bold" /> The Reality of Arbitrage
        </h3>
        <div className="space-y-3">
          <p className="text-xs text-zinc-300 leading-relaxed">
            <strong className="text-zinc-100">Arbitrage is real, but it's not easy money.</strong> The days of manually buying Bitcoin on one exchange and selling on another for a 5% profit are mostly over. Markets have become efficient, and professional trading firms with millions in capital and custom infrastructure capture most opportunities before retail traders can react.
          </p>
          <p className="text-xs text-zinc-300 leading-relaxed">
            <strong className="text-zinc-100">Where retail traders can still find edges:</strong> Small-cap DeFi tokens with low liquidity, new chain launches where prices haven't stabilized, stablecoin depegs during market panics, and niche markets that institutional bots don't cover yet. But always account for fees, slippage, and execution risk.
          </p>
          <p className="text-xs text-zinc-300 leading-relaxed">
            <strong className="text-zinc-100">The best mindset:</strong> Think of arbitrage as a skill set and market understanding, not a get-rich-quick strategy. Understanding how and why prices differ across markets makes you a better trader overall — even if you never execute a single arbitrage trade.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   MAIN PAGE — Tabbed Layout
   ============================================================ */
const SUB_TABS = [
  { key: "leverage", label: "Leverage", icon: Scales },
  { key: "how-to", label: "How to Leverage", icon: Handshake },
  { key: "arbitrage", label: "Arbitrage", icon: ArrowsLeftRight },
];

export default function Leverage() {
  const [tab, setTab] = useState("leverage");

  return (
    <div data-testid="leverage-page">
      <div className="bg-zinc-950 border-b border-zinc-800/60 px-5 pt-4 pb-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <Scales size={22} weight="bold" className="text-orange-400" />
            <h1 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">Leverage</h1>
            <Badge className="text-[8px] px-1.5 py-0.5 border-0 bg-orange-900/30 text-orange-400 h-4">EDUCATION</Badge>
          </div>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-9 bg-zinc-900/60 border border-zinc-800/60 p-0.5 gap-0.5" data-testid="leverage-tabs">
            {SUB_TABS.map(t => {
              const Icon = t.icon;
              return (
                <TabsTrigger key={t.key} value={t.key} data-testid={`leverage-tab-${t.key}`}
                  className="text-[11px] px-3 py-1.5 h-8 gap-1.5 font-medium transition-colors data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-50 data-[state=active]:shadow-sm">
                  <Icon size={13} weight="bold" />
                  {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
      </div>

      <div data-testid={`leverage-content-${tab}`}>
        {tab === "leverage" && <LeverageTab />}
        {tab === "how-to" && <HowToLeverageTab />}
        {tab === "arbitrage" && <ArbitrageTab />}
      </div>
    </div>
  );
}
